import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setUnauthenticatedHandler } from './api';
import { setDisplayTimeZone } from './format';
import type { Me, Permission, PropertySummary } from './types';

/** Permissions of the default roles (PRD roles table), used when the server does not send a list (older API, tests). */
export const DEFAULT_PERMISSIONS: Record<string, Permission[]> = {
  housekeeping: ['stock_move'],
  housekeeping_mgr: ['stock_move', 'purchase', 'items_add', 'suppliers_add', 'par_levels', 'reports'],
  ops_manager: ['stock_move', 'waste_approve', 'adjust', 'purchase', 'items_add', 'items_edit', 'suppliers_add', 'par_levels', 'settings', 'reports', 'audit'],
  procurement: ['purchase', 'purchase_manage', 'items_add', 'items_edit', 'suppliers_add', 'suppliers_edit', 'reports'],
  finance: ['waste_coapprove', 'reports', 'audit', 'period_close'],
  viewer: ['reports'],
};
const PROPERTY_KEY = 'h101-property';

interface Session {
  me: Me | null;
  loading: boolean;
  /** The session ended (e.g. timed out) while the app was open; shown as a sign-in dialog over the page. */
  expired: boolean;
  property: PropertySummary | null;
  setPropertyId: (id: string) => void;
  /** A permission, or 'admin' for the built-in Admin role (all properties, properties page, period reopen). */
  can: (what: Permission | 'admin') => boolean;
  refresh: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

function readStored() {
  try {
    return localStorage.getItem(PROPERTY_KEY);
  } catch {
    return null;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [propertyId, setPropertyIdState] = useState<string | null>(readStored);

  const refresh = useCallback(async () => {
    try {
      setMe(await api.get<Me>('/auth/me'));
      setExpired(false);
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Keep the current page (and any unsaved input) mounted; App shows a sign-in dialog on top.
    setUnauthenticatedHandler(() => setExpired(true));
    void refresh();
  }, [refresh]);

  const setPropertyId = useCallback((id: string) => {
    setPropertyIdState(id);
    try {
      localStorage.setItem(PROPERTY_KEY, id);
    } catch {
      /* storage unavailable: selection lasts for this page only */
    }
  }, []);

  const value = useMemo<Session>(() => {
    const properties = me?.properties ?? [];
    const property = properties.find(p => p.id === propertyId) ?? properties[0] ?? null;
    setDisplayTimeZone(property?.timezone);
    return {
      me, loading, expired, property, setPropertyId, refresh,
      can: what => {
        if (!me) return false;
        if (me.user.role === 'admin') return true;
        if (what === 'admin') return false;
        return (me.user.permissions ?? DEFAULT_PERMISSIONS[me.user.role] ?? []).includes(what);
      },
      signIn: async (email, password) => {
        await api.post('/auth/login', { email, password });
        await refresh();
      },
      signOut: async () => {
        await api.post('/auth/logout').catch(() => {});
        setMe(null);
        setExpired(false);
      },
    };
  }, [me, loading, expired, propertyId, setPropertyId, refresh]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

/** The selected property; pages under the app shell can rely on it being set. */
export function useProperty() {
  const { property } = useSession();
  if (!property) throw new Error('No property selected');
  return property;
}
